import { authForgetPassword, authVerifyForgetPasswordToken } from '@/redux/actions';
import * as Constants from '@/redux/constants';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Alert, CircularProgress, Divider, Link, Stack, Typography } from '@mui/material';
import { useDialogs } from '@toolpad/core';
import icon from 'assets/login-page-icon.png';
import { Button } from 'components/button';
import { TextInput } from 'components/input';
import { LanguageSwitcher } from 'components/language-switcher';
import { useDispatch, useLanguageNavigate, useLocale, useSelector } from 'hooks';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { shallowEqual } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

const selector = (state) => ({
  authVerifyForgetPasswordTokenInfo: state.authVerifyForgetPasswordTokenInfo,
  authForgetPasswordInfo: state.authForgetPasswordInfo,
});

function VerifyForgotPasswordContent() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const dispatch = useDispatch();
  const dialogs = useDialogs();
  const locale = useLocale();
  const t = useI18n();
  const navigate = useLanguageNavigate();
  const { authVerifyForgetPasswordTokenInfo, authForgetPasswordInfo } = useSelector(selector, shallowEqual);
  const { formState: { errors }, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      email: email || '',
      token: '',
    },
  });

  const [resendCooldown, setResendCooldown] = useState(0);
  const isFetching = authVerifyForgetPasswordTokenInfo.isFetching;
  const isResending = authForgetPasswordInfo.isFetching;

  useEffect(() => {
    if (email) {
      setValue('email', email);
    } else {
      navigate('/forgot-password');
    }
  }, [email, setValue, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    switch (authVerifyForgetPasswordTokenInfo.type) {
      case Constants.AUTH_VERIFY_FORGET_PASSWORD_TOKEN.SUCCESS: {
        // Store the verified token and redirect to reset password page
        const verifiedToken = get(authVerifyForgetPasswordTokenInfo, 'data.token');
        navigate(`/forgot-password/reset?email=${email}&token=${verifiedToken}`);
        break;
      }
    }
  }, [authVerifyForgetPasswordTokenInfo, navigate, email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    switch (authForgetPasswordInfo.type) {
      case Constants.AUTH_FORGET_PASSWORD.SUCCESS:
        dialogs.alert(t('A new verification code has been sent to your email.'), {
          title: t('Code Resent'),
          okText: t('OK')
        });
        // Start 60-second cooldown
        setResendCooldown(60);
        break;
      case Constants.AUTH_FORGET_PASSWORD.FAILURE:
        dialogs.alert(
          get(authForgetPasswordInfo, 'error.error.description', t('Failed to resend verification code. Please try again.')),
          {
            title: t('Error'),
            okText: t('OK')
          }
        );
        break;
    }
  }, [authForgetPasswordInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const onSubmit = useCallback((data) => {
    dispatch(authVerifyForgetPasswordToken({
      payload: {
        email: data.email,
        token: data.token,
      }
    }));
  }, [dispatch]);

  const onError = useCallback(() => {
    dialogs.alert(t('Please enter a valid 6-digit verification code.'), {
      title: t('Invalid Input'),
      okText: t('OK')
    });
  }, [dialogs, t]);

  const handleResendCode = useCallback(() => {
    if (resendCooldown > 0) return;

    const currentEmail = watch('email');
    if (!currentEmail) {
      dialogs.alert(t('Email is required to resend verification code.'), {
        title: t('Error'),
        okText: t('OK')
      });
      return;
    }

    dispatch(authForgetPassword({
      payload: {
        email: currentEmail
      }
    }));
  }, [dispatch, resendCooldown, watch, dialogs]);

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit, onError)}
      gap={4}
      sx={{
        backgroundColor: 'background.default',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        position: 'relative',
      }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%', mt: -2, px: 2 }}>
        <LanguageSwitcher />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: '280px' }}>
        <img src={icon} alt='icon' style={{ width: '100px', height: '100px' }} />

        <Typography variant='h5' color='secondary.main' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {t('GIVING')}
        </Typography>
      </Stack>

      <Stack sx={{ width: '500px', flexDirection: 'column', display: 'flex', alignItems: 'center' }}>
        <Typography variant='h2' color='secondary.main' sx={{ p: 2, fontWeight: 'bold' }}>
          {t('VERIFY CODE')}
        </Typography>
        <Divider sx={{ width: '90%', height: '4px', backgroundColor: 'primary.main' }} />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: '500px' }}>
        <Typography variant='body1' sx={{ textAlign: 'center', px: 2 }}>
          {t('A 6-digit verification code has been sent to your email address. Please enter the code below to continue.')}
        </Typography>

        {authVerifyForgetPasswordTokenInfo.error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {get(authVerifyForgetPasswordTokenInfo, 'error.error.description', t('Verification failed. Please check your code and try again.'))}
          </Alert>
        )}

        <TextInput
          label={t('Email')}
          type="email"
          value={watch('email')}
          onChange={(e) => setValue('email', e.target.value)}
          error={!!errors.email}
          helper={errors.email?.message}
          fullWidth
          readOnly
          sx={{ backgroundColor: 'action.disabledBackground' }}
        />

        <TextInput
          label={t('6-Digit Verification Code')}
          type="text"
          value={watch('token')}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
            setValue('token', value);
          }}
          error={!!errors.token}
          helper={errors.token?.message}
          fullWidth
          inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
          placeholder={t('000000')}
        />

        <Stack direction="row" gap={1} sx={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Typography variant='body2'>
            {t('Didn\'t receive the code?')}
          </Typography>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || isResending}
            sx={{
              cursor: resendCooldown > 0 || isResending ? 'not-allowed' : 'pointer',
              opacity: resendCooldown > 0 || isResending ? 0.5 : 1,
            }}
          >
            {isResending ? t('Resending...') : resendCooldown > 0 ? t('Resend in {{count}}s', { count: resendCooldown }) : t('Resend Code')}
          </Link>
        </Stack>
      </Stack>

      <Stack sx={{ display: 'flex', width: '300px' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          leading={faCheckCircle}
          fullWidth
          disabled={isFetching || watch('token')?.length !== 6}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {isFetching ? t('Verifying...') : t('Verify Code')}
        </Button>
      </Stack>

      <Typography variant='body2'>
        <a href={`/giving/forgot-password?locale=${locale}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
          {t('Back to Forgot Password')}
        </a>
      </Typography>
    </Stack >
  );
}

function VerifyForgotPasswordPage() {
  return (
    <Suspense fallback={
      <Stack
        sx={{
          backgroundColor: 'background.default',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <CircularProgress />
      </Stack>
    }>
      <VerifyForgotPasswordContent />
    </Suspense>
  );
}

export default VerifyForgotPasswordPage;
